import React, {useEffect, useState, useMemo} from "react";
import _ from "lodash"
import {useNavigate, useParams} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {useSelector, useDispatch} from 'react-redux';
import {styled, useTheme} from '@mui/material/styles';
import {routes} from "routes";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import {Box} from "@mui/system";
import { PageHeader } from "components/molecules/PageHeader/PageHeader";
import {Paper as CustomPaper} from "components/atoms/Paper/Paper";
import {Card, MenuList, Switch, Pagination} from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import Typography from "@mui/material/Typography";
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Stack from "@mui/material/Stack";
import { Table, TableBody, TableCell, TableHead, TableRow, tableCellClasses } from '@mui/material';
import Tabs, {tabsClasses} from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TabPanel from "components/atoms/TabPanel/TabPanel";
import { DataGrid } from '@mui/x-data-grid';
import CallMissedOutgoingOutlinedIcon from '@mui/icons-material/CallMissedOutgoingOutlined';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import useMediaQuery from "@mui/material/useMediaQuery";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {selectStrategyById, togglePublicState} from "redux/strategy/strategySlice";
import {fetchTrades, fetchClosedTrades, selectAllTrades, selectClosedTrades, selectActiveTrades, selectTradePagination, selectTradeLoading, selectClosedTradesLoading} from "redux/trade/tradeSlice";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {createSubscription, selectSubscriptionByStrategyId} from "redux/subscription/subscriptionSlice";
import StyledCardComponent from "components/molecules/StyledCardElement/StyledCardComponent";
import LinearProgress from '@mui/material/LinearProgress';
import {selectLinkedAccountByBrokerID} from "redux/linkedAccount/linkedAccountSlice";
import {deleteSimulation, selectSimulationByStrategyId} from "redux/simulation/simulationSlice";
import {useConfirmation} from "utils/ConfirmationProvider";
import { ResultsDisclaimer } from "components/molecules/ResultsDisclaimer/ResultsDisclaimer";
import HypotheticalPerformance from "components/organisms/Strategy/HypotheticalPerformance";
import { fetchOrders } from "redux/order/orderSlice";
import { fetchStrategyPerformance, selectStrategyPerformanceData } from "redux/strategy/performanceSlice";
import StrategyStatistics from "components/molecules/StrategyStatistics/StrategyStatistics";

const LargeWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

// Styles for P/L values
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  '& .positive-value': {
    color: theme.palette.success.dark
  },
  '& .negative-value': {
    color: theme.palette.error.main
  },
  '& .MuiDataGrid-main': {
    backgroundColor: 'white'
  },
  '& .MuiDataGrid-row': {
    backgroundColor: 'white'
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[100]
  },
  '& .MuiDataGrid-footerContainer': {
    backgroundColor: 'transparent'
  }
}));

/**
 * StrategyDetails Component
 * Main component for displaying detailed information about a trading strategy
 * Includes performance metrics, trade history, and subscription management
 */
export default function StrategyDetails() {
  // Theme and navigation hooks
  const theme = useTheme();
  const params = useParams();
  const navigate = useNavigate();
  const confirm = useConfirmation()
  const isAuthorised = useSelector(state => state.global.isAuthorised);
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const strategyId = params.strategyId;
  const strategy = useSelector((state) => selectStrategyById(state, strategyId));
  const subscription = useSelector((state) => selectSubscriptionByStrategyId(state, strategyId));
  const simulation = useSelector((state) => selectSimulationByStrategyId(state, strategyId));
  const linked_account = useSelector((state) => selectLinkedAccountByBrokerID(state, strategy?.broker?.type));
  const stripe = useStripe();
  const elements = useElements();
  const trades = useSelector(selectAllTrades);
  const closedTrades = useSelector(selectClosedTrades);
  const activeTrades = useSelector(selectActiveTrades);
  
  // Get performance data from Redux
  const performanceData = useSelector((state) => selectStrategyPerformanceData(state, strategyId));
  
  // Local state management
  const [value, setValue] = React.useState(0);  // Current tab index
  const [anchorEl, setAnchorEl] = React.useState(null);  // Menu anchor element
  const [openIndex, setOpenIndex] = React.useState(null);  // Active menu index
  const [subscribeSubmitting, setSubscribeSubmitting] = useState(false);  // Subscription form state
  const [subscribeError, setSubscribeError] = useState(null);  // Subscription error state
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);  // Subscription dialog visibility
  
  const profile = useSelector(state => state.userProfile.data);
  const canTrade = profile && (profile.user_type === 'TRADER' || profile.user_type === 'ADMIN');
  
  const tradesLoading = useSelector(selectTradeLoading);
  const closedTradesLoading = useSelector(selectClosedTradesLoading);
  const pagination = useSelector(selectTradePagination);
  
  // Local state for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Initial data fetching and performance data
  useEffect(() => {
    // Fetch performance data (one-time)
    dispatch(fetchStrategyPerformance(strategyId));
  }, [dispatch, strategyId]);
  
  // Handle active trades fetching with polling
  useEffect(() => {
    // Initial fetch of active trades
    dispatch(fetchTrades({ strategyId, status: 'ACTIVE', refresh: true }));
    
    // Set up polling interval for active trades only
    const intervalId = setInterval(() => {
      dispatch(fetchTrades({ strategyId, status: 'ACTIVE' }));
    }, 10000); // Poll every 10 seconds

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [dispatch, strategyId]);
  
  // Handle closed trades fetching (only when pagination changes)
  useEffect(() => {
    dispatch(fetchClosedTrades({ strategyId, page, pageSize }));
  }, [dispatch, strategyId, page, pageSize]);
  

  // Event handlers for subscription management
  const handleOpenSubscriptionDialog = () => {
    if (_.isEmpty(linked_account)) {
      confirm({
        title: `Connect a Broker Account`,
        message: <>In order to subscribe to <b>{strategy.name}</b> you need to connect to your <b>{strategy.broker.name}</b> account.</>,
        onConfirm: async () => {
          navigate(`${routes.brokerAccountConnect}?broker=${strategy.broker.type}`)
        },
        maxWidth: "md"
      });
    } else {
      if(linked_account[0].status === 'pending') {
        confirm({
          title: `Broker Account is awaiting approval`,
          message: <>You <b>{strategy.broker.name}</b> account is awaiting approval. <br></br> Please wait for the account to be activated. </>,
          onConfirm: async () => {},
          maxWidth: "md"
        });
      } else {
        setSubscribeError(false)
        setOpenSubscriptionDialog(true);
      }
    }
  }

  const handleCloseSubscriptionDialog = () => {
    setOpenSubscriptionDialog(false);
  };

  const handleSubmitSubscription = async (event) => {
    setSubscribeSubmitting(true)
    setSubscribeError(false)
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    const card = elements.getElement(CardElement);
    const result = await stripe.createToken(card);
    if (result.error) {
      setSubscribeError(result.error.message)
      setSubscribeSubmitting(false)
      return;
    }
    const token = result.token;
    const subscriptionData = {
      token: token.id,
      strategy: strategy.id,
      linked_account: linked_account[0].id
    };
    if(!_.isEmpty(simulation)) {
      await dispatch(deleteSimulation(simulation[0].id));
    }
    await dispatch(createSubscription(subscriptionData)).unwrap();
    setSubscribeSubmitting(false)
    setOpenSubscriptionDialog(false)
    enqueueSnackbar('You have subscribed successfully.', { variant: 'success' });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleClick = (event) => {
    setOpenIndex(parseInt(event.currentTarget.getAttribute('data-index')))
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setOpenIndex(null);
  };

  const togglePublic = async () => {
    await dispatch(togglePublicState(strategyId)).unwrap();
    enqueueSnackbar(`Strategy has been set to "${strategy.public? 'Private': 'Public'}".`, { variant: 'success' });
  }

  // Column definitions for trade tables
  const open_position_columns = [
    { field: 'created', headerName: 'Date', flex: 1, minWidth: 200, valueFormatter: (params) => {
      return new Date(params.value).toLocaleString();
    }},
    { field: 'symbol', headerName: 'Symbol', flex: 0.7, minWidth: 120 },
    { field: 'trade_type', headerName: 'Side', flex: 0.7, minWidth: 100, valueFormatter: (params) => {
      return params.value?.charAt(0).toUpperCase() + params.value?.slice(1) || 'N/A';
    }},
    { field: 'quantity', headerName: 'Quant', flex: 0.7, minWidth: 100, valueFormatter: (params) => {
      return params.value?.toLocaleString() || 'N/A';
    }},
    { field: 'average_entry_price', headerName: 'Basis', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      return params.value ? `$${params.value.toLocaleString()}` : 'N/A';
    }},
    { field: 'last_price', headerName: 'Price', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      return params.value ? `$${params.value.toLocaleString()}` : 'N/A';
    }},
    { field: 'unrealized_pnl', headerName: 'Unrealized P/L', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      if (params.value === null || params.value === undefined) return 'N/A';
      const value = typeof params.value === 'string' ? parseFloat(params.value) : Number(params.value);
      if (isNaN(value)) return 'N/A';
      
      if (value >= 0) {
        return `$${value.toFixed(2)}`;
      } else {
        return `($${Math.abs(value).toFixed(2)})`;
      }
    }, cellClassName: (params) => {
      if (params.value === null || params.value === undefined) return '';
      const value = typeof params.value === 'string' ? parseFloat(params.value) : Number(params.value);
      return value >= 0 ? 'positive-value' : 'negative-value';
    }}
  ];

  const trade_columns = [
    { field: 'created', headerName: 'Opened Date/Time', flex: 1, minWidth: 200, valueFormatter: (params) => {
      return new Date(params.value).toLocaleString();
    }},
    { field: 'symbol', headerName: 'Symbol', flex: 0.7, minWidth: 120 },
    { field: 'trade_type', headerName: 'Side', flex: 0.7, minWidth: 100, valueFormatter: (params) => {
      return params.value?.charAt(0).toUpperCase() + params.value?.slice(1) || 'N/A';
    }},
    { field: 'max_quantity', headerName: 'Quant', flex: 0.7, minWidth: 100, valueFormatter: (params) => {
      return params.value?.toLocaleString() || 'N/A';
    }},
    { field: 'average_entry_price', headerName: 'Entry Price', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      return params.value ? `$${params.value.toLocaleString()}` : 'N/A';
    }},
    { field: 'closed_at', headerName: 'Closed Date/Time', flex: 1, minWidth: 200, valueFormatter: (params) => {
      return params.value ? new Date(params.value).toLocaleString() : 'N/A';
    }},
    { field: 'closing_price', headerName: 'Exit Price', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      return params.value ? `$${params.value.toLocaleString()}` : 'N/A';
    }},
    { field: 'realized_pnl', headerName: 'P/L', flex: 0.7, minWidth: 120, valueFormatter: (params) => {
      if (params.value === null || params.value === undefined) return 'N/A';
      const value = typeof params.value === 'string' ? parseFloat(params.value) : Number(params.value);
      if (isNaN(value)) return 'N/A';
      
      if (value >= 0) {
        return `$${value.toFixed(2)}`;
      } else {
        return `($${Math.abs(value).toFixed(2)})`;
      }
    }, cellClassName: (params) => {
      if (params.value === null || params.value === undefined) return '';
      const value = typeof params.value === 'string' ? parseFloat(params.value) : Number(params.value);
      return value >= 0 ? 'positive-value' : 'negative-value';
    }}
  ];

  return (
    <>
      {strategy && <>
        {/* Strategy Management Menu - Only visible to strategy owners */}
        {strategy.owned && <>
               <Menu anchorEl={anchorEl} open={openIndex === 0 } onClose={handleClose}  anchorOrigin={{ vertical: 'bottom', horizontal: 'left', }} transformOrigin={{ vertical: 'top', horizontal: 'left', }}   >
                <MenuList sx={{ pt:0, pb: 0}}>
                  {canTrade && (
                    <MenuItem onClick={() => navigate(routes.strategyWebTrader.replace(":strategyId", strategy.id))}  >Enter new trade</MenuItem>
                  )}
                  <MenuItem onClick={() => navigate(routes.strategyFormEdit.replace(":strategyId", strategy.id))}>Edit your strategy</MenuItem>
                </MenuList>
              </Menu>
                <Menu anchorEl={anchorEl} open={openIndex === 1} onClose={handleClose}   anchorOrigin={{ vertical: 'bottom', horizontal: 'left', }} transformOrigin={{ vertical: 'top', horizontal: 'left', }} >
                  <MenuList sx={{ pt:0, pb: 0}}>
                    <MenuItem onClick={() => navigate(routes.strategySubscribers.replace(":strategyId", strategy.id))}>List subscribers</MenuItem>
                  </MenuList>
                </Menu>


              <Box  sx={{ mb: 5, display: {xs: 'none', sm: 'block'}}} >
          <ButtonGroup orientation="horizontal" variant="outlined" >
            {canTrade && (
              <Button variant={"outlined"} onClick={() => navigate(routes.strategyWebTrader.replace(":strategyId", strategy.id))} startIcon={<CallMissedOutgoingOutlinedIcon />} >Enter Trade</Button>
            )}
            <Button onClick={handleClick} data-index={0} variant="outlined" startIcon={<SettingsOutlinedIcon/>}endIcon={<KeyboardArrowDownIcon/>} size={mobile? "large": "small"}> Manage </Button>
            <Button onClick={handleClick} data-index={1} variant="outlined" startIcon={<GroupAddOutlinedIcon/>} endIcon={<KeyboardArrowDownIcon/>} size={mobile? "large": "small"}> Subscribers </Button>
          </ButtonGroup>

        </Box>


          </>}

      <Box sx={{ mb: 5, display: { xs: "block", sm: "flex"}}} justifyContent="space-between" alignItems="center">
        <PageHeader text1={strategy.name} text2={"strategy"} description={"These are performance results that have certain inherent limitations."}/>
        {strategy.owned ?
         <Card sx={{p: 1, pl: 2, pr: 2, mt: { xs: 5, sm: 0}}}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>Private</Typography>
            <Switch color="secondary" checked={strategy.public} onClick={() => togglePublic()}/>
            <Typography>Public</Typography>
          </Stack>
        </Card> :
          <>
          {_.isEmpty(subscription) ?
            <>
              <Button 
                variant="contained" 
                color="secondary" 
                sx={{ 
                  mt: 3,
                  width: { xs: '100%', sm: 'auto' }
                }} 
                onClick={handleOpenSubscriptionDialog} 
                disabled={!isAuthorised}
              >
                <Typography sx={{ textTransform: 'none' }}>
                 <b>Subscribe</b> <br />
                 <small>Full access for ${strategy.charge}/mo</small>
                </Typography>
              </Button>

              <Dialog
                fullWidth={true}
                open={openSubscriptionDialog}
                onClose={handleCloseSubscriptionDialog}
              >
                <DialogTitle>
                  Subscribe
                </DialogTitle>
                <DialogContent>
                  <StyledCardComponent onCardChange={() => setSubscribeError(null)} />
                  {setSubscribeError && <div style={{ color: 'red' }}>{subscribeError}</div>}

                  <Grid container>
                    <Grid item sm={6}></Grid>
                    <Grid item sm={6} sx={{mt: 3}}>
                      <Table size="small" align="right"  sx={{ [`& .${tableCellClasses.root}`]: { borderBottom: "none" } }}>
                        <TableBody>
                          <TableRow align="right">
                            <TableCell align="right">Strategy Name</TableCell>
                            <TableCell align="right"><Typography fontWeight={500} variant="span">{strategy.name}</Typography></TableCell>
                          </TableRow>
                            <TableRow>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="right"><Typography fontWeight={500} variant="span">{strategy.charge} $</Typography></TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="right">Recurrence</TableCell>
                                <TableCell align="right"><Typography fontWeight={500} variant="span">Monthly</Typography></TableCell>
                            </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{p: 3}}>
                    <Button onClick={handleCloseSubscriptionDialog} color="primary">
                        Close
                    </Button>
                  <Button onClick={handleSubmitSubscription} color="secondary" variant="contained" disabled={subscribeSubmitting}>
                      Subscribe
                  </Button>
                </DialogActions>
                {subscribeSubmitting && <Box sx={{ width: '100%' }}> <LinearProgress color="secondary"/> </Box>}
              </Dialog> 
            </>
            :
            <>
              {/* Subscribed button with dropdown */}
              <Box sx={{ 
                mt: 3, 
                position: 'relative',
                width: { xs: '100%', sm: 'auto' } 
              }}>
                <ButtonGroup 
                  variant="contained" 
                  color="success"
                  sx={{ 
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  <Button sx={{ 
                    flex: { xs: 1, sm: 'initial' },
                    width: { xs: '100%', sm: 'auto' }
                  }}>
                    <Typography sx={{ textTransform: 'none' }}>
                      <b>Subscribed</b> <br />
                      <small>${strategy.charge}/mo</small>
                    </Typography>
                  </Button>
                  <Button 
                    size="small" 
                    onClick={handleClick} 
                    data-index={2}
                    aria-label="subscription options"
                  >
                    <KeyboardArrowDownIcon />
                  </Button>
                </ButtonGroup>
                <Menu 
                  anchorEl={anchorEl} 
                  open={openIndex === 2} 
                  onClose={handleClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={() => {
                    handleClose();
                    navigate(`${routes.account}?tab=subscriptions`);
                  }}>
                    Cancel Subscription
                  </MenuItem>
                </Menu>
              </Box>
            </>
          }
          </>
        }

      </Box>

       <Box  sx={{ mb: 5, display: {xs: 'block', sm: 'none'}}} >
            {strategy.owned ? (
              <ButtonGroup orientation="vertical" variant="outlined" fullWidth>
                <Button variant={"outlined"} onClick={() => navigate(routes.strategyWebTrader.replace(":strategyId", strategy.id))} startIcon={<CallMissedOutgoingOutlinedIcon />} size="large">Enter Trade</Button>
                <Button onClick={handleClick} data-index={0} variant="outlined" size="large" startIcon={<SettingsOutlinedIcon/>} endIcon={<KeyboardArrowDownIcon/>} > Manage </Button>
                <Button onClick={handleClick} data-index={1} variant="outlined" size="large" startIcon={<GroupAddOutlinedIcon/>} endIcon={<KeyboardArrowDownIcon/>} > Subscribers </Button>
              </ButtonGroup>
            ) : (
              null
            )}
       </Box>

    {strategy.owned === false &&
      <Box sx={{ flexGrow: 1, mb: 5 }}>
        <StrategyStatistics 
          strategy={strategy}
          metrics={['annual_return', 'drawdown', 'num_trades', 'profit_percent', 'category', 'win_months']}
          loading={!performanceData}
        />
      </Box>
    }

      <Box >
        <Grid container spacing={2}>
          <HypotheticalPerformance 
            strategyName={strategy.name} 
            performanceData={performanceData}
            metrics={{
              ...strategy.performance_metrics,
              min_capital: strategy.min_capital ? parseFloat(strategy.min_capital) : null
            }}
            loading={!performanceData}
          />
        </Grid>

        <Grid container>
          <Grid xs={12} sm={12} sx={{ mt: 10, mb: 5}}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  '&.Mui-disabled': { opacity: 0.3 },
                },
                backgroundColor: 'transparent',
                '& .MuiTab-root': {
                  minHeight: '40px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'text.primary',
                  backgroundColor: 'transparent',
                  margin: '0 4px',
                  borderRadius: '6px 6px 0 0',
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid transparent',
                  position: 'relative',
                  '&.Mui-selected': {
                    color: 'white',
                    backgroundColor: 'secondary.main',
                    borderColor: 'secondary.main',
                  },
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                    color: 'white',
                    borderColor: 'secondary.dark',
                  },
                  '&:first-of-type': {
                    marginLeft: 0,
                  },
                  '&:last-of-type': {
                    marginRight: 0,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: 'secondary.dark',
                  height: 3,
                },
              }}
            >
              <Tab label="Trade Record" />
              <Tab label="Description" />
            </Tabs>

            <Box sx={{m: 1, mt: 3}}>
              <TabPanel name="trade-record" value={value} index={0}>
                {strategy.owned && (
                  <>
                    <Typography 
                      variant="h6" 
                      component="div" 
                      sx={{ 
                        color: theme.palette.primary.light,
                        mb: 2
                      }}
                    >
                      Open Positions
                    </Typography>
                    {activeTrades.length > 0 ? (
                      <StyledDataGrid
                        rows={activeTrades}
                        columns={open_position_columns}
                        initialState={{
                          pagination: {
                            paginationModel: { page: 0, pageSize: 5 },
                          },
                          sorting: {
                            sortModel: [{ field: 'created', sort: 'desc' }],
                          },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        getRowId={(row) => row.id}
                        autoHeight
                        sx={{ mb: 4 }}
                      />
                    ) : (
                      <Box sx={{ mb: 5 }}>
                        <Typography>No open positions.</Typography>
                      </Box>
                    )}
                  </>
                )}

                <Typography variant="h6" gutterBottom>Trading Record</Typography>
                <StyledDataGrid
                  rows={closedTrades}
                  columns={trade_columns}
                  initialState={{
                    pagination: {
                      paginationModel: { page: page - 1, pageSize },
                    },
                    sorting: {
                      sortModel: [{ field: 'created', sort: 'desc' }],
                    },
                  }}
                  paginationMode="server"
                  rowCount={pagination.count}
                  pageSizeOptions={[10, 25, 50]}
                  onPaginationModelChange={(model) => {
                    setPage(model.page + 1); // MUI DataGrid uses 0-based indexing
                    setPageSize(model.pageSize);
                    dispatch(fetchClosedTrades({ 
                      strategyId, 
                      page: model.page + 1, 
                      pageSize: model.pageSize 
                    }));
                  }}
                  getRowId={(row) => row.id}
                  loading={closedTradesLoading}
                  autoHeight
                />
              </TabPanel>
              <TabPanel name="account" value={value} index={1}>
                <Typography variant="h6" gutterBottom>Strategy Description</Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {strategy.description || 'No description available for this strategy.'}
                </Typography>
              </TabPanel>
              </Box>
            </Grid>
          </Grid>

        </Box>

       <Box sx={{mt: 5}}>
       <ResultsDisclaimer/>
      </Box>

        </>}
    </>
  )
}
